import { useDeferredValue, useMemo } from 'react';
import type { Guide } from '../../animation/animationController';
import { MotionError } from '../../animation/procedural/compiler';
import { applyMotionPlan, type MotionRequest, type MotionResult } from '../../domain/motionOps';
import type { Animation, Project, Scene } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import type { ComposerState } from '../../state/motionComposer';
import { useServices } from '../../state/services';
import type { PlannedOverlay } from './TimelinePanel';

export interface MotionPreview {
  /** The plan applied under the chosen policy (null when it cannot be applied). */
  result: MotionResult | null;
  error: { message: string; issues: string[] } | null;
  /** Planned keys and conflicts for the timeline. */
  overlay: PlannedOverlay | null;
  /** Paths and trajectories for the canvas. */
  guides: Guide[];
  /** The uncommitted animation to display while previewing (null: show the real one). */
  animation: Animation | null;
  /** Compile time of this preview in ms. */
  ms: number;
  request: MotionRequest | null;
}

const NONE: MotionPreview = { result: null, error: null, overlay: null, guides: [], animation: null, ms: 0, request: null };

export function motionRequest(state: ComposerState, scene: Scene, active: Animation | null, project: Project): MotionRequest | null {
  if (!state.steps.length) return null;
  const proposed = state.animationId ? project.animations.find((a) => a.id === state.animationId && a.sceneId === scene.id) : undefined;
  return {
    plan: { version: 1, slots: state.slots, steps: state.steps, seed: state.seed, totalSeconds: state.totalSeconds },
    binding: state.binding,
    policy: state.policy,
    allowExtend: state.allowExtend,
    sceneId: scene.id,
    animationId: proposed?.id ?? active?.id ?? null,
    newAnimation: { name: state.newAnimationName.trim() || undefined },
  };
}

/**
 * Compiles the composer's plan against the current project for preview. Nothing is
 * committed and nothing enters undo history. When the chosen policy refuses the plan
 * (conflicts), the overlay still shows what would be written and what conflicts.
 */
export function useMotionPreview(project: Project, scene: Scene, active: Animation | null): MotionPreview {
  const { motion } = useServices();
  const state = useDeferredValue(useObservable(motion));
  return useMemo(() => computePreview(state, scene, active, project), [state, scene, active, project]);
}

/** The preview for a composer state (pure apart from timing the compile). */
export function computePreview(state: ComposerState, scene: Scene, active: Animation | null, project: Project): MotionPreview {
  const request = motionRequest(state, scene, active, project);
  if (!request) return NONE;
  const t0 = performance.now();
  let result: MotionResult | null = null;
  let shown: MotionResult | null = null;
  let error: MotionPreview['error'] = null;
  try {
    result = shown = applyMotionPlan(project, request);
  } catch (e) {
    error = { message: e instanceof Error ? e.message : String(e), issues: e instanceof MotionError ? e.issues : [] };
    // Show what the plan would write (and its conflicts) even though this policy refuses it.
    if (request.policy === 'merge' || request.policy === 'append') {
      try {
        shown = applyMotionPlan(project, { ...request, policy: 'replace-range' });
      } catch {
        shown = null;
      }
    }
  }
  const ms = performance.now() - t0;
  if (!shown) return { ...NONE, error, ms, request };
  const conflicts = new Set(shown.conflicts.flatMap((c) => c.frames.map((f) => `${c.objectId}/${c.channel}@${f}`)));
  const overlay: PlannedOverlay = { tracks: shown.compiled.tracks.map((t) => ({ objectId: t.objectId, channel: t.channel, frames: t.keyframes.map((k) => k.frame) })), conflicts, endFrame: shown.compiled.endFrame };
  const guides: Guide[] = [...shown.compiled.trails.map((t) => ({ points: t.points, kind: 'trail' as const })), ...shown.compiled.paths.map((p) => ({ points: p.points, kind: 'path' as const }))];
  return { result, error, overlay, guides, animation: shown.animation, ms, request };
}

